package com.havn.app.features.reports.data

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.supabase.HavnSupabaseConfig
import com.havn.app.features.reports.domain.SellerReportAiInput
import com.havn.app.features.reports.domain.SellerReportAiOpenHouse
import com.havn.app.features.reports.domain.SellerReportAiService
import com.havn.app.features.reports.domain.SellerReportAiTotals
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.auth.auth
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlin.time.Clock

class SupabaseSellerReportAiService(
    private val supabaseClient: SupabaseClient,
    private val httpClient: HttpClient = sellerReportAiHttpClient(),
    private val json: Json = Json { ignoreUnknownKeys = true; prettyPrint = true }
) : SellerReportAiService {
    override suspend fun generateInsights(input: SellerReportAiInput): Result<String> {
        return runCatching {
            val requestId = sellerReportAiRequestId()
            val startedAtMs = Clock.System.now().toEpochMilliseconds()
            val accessToken = supabaseClient.auth.currentAccessTokenOrNull()
                ?: error("Sign in again before generating seller report AI insights.")
            val requestBody = json.encodeToString(SellerReportAiRequest.serializer(), input.toRequest())
            HavnLogger.info(
                "SellerReportsAI",
                "[$requestId] http start language=${input.language} openHouses=${input.openHouses.size} visitors=${input.totals.visitors} notes=${input.agentNotes.size} bodyChars=${requestBody.length}"
            )
            val httpResponse = try {
                httpClient.post("${HavnSupabaseConfig.url}/functions/v1/generate-seller-report-insights") {
                    contentType(ContentType.Application.Json)
                    header(HttpHeaders.Authorization, "Bearer $accessToken")
                    header("apikey", HavnSupabaseConfig.anonKey)
                    setBody(requestBody)
                }
            } catch (throwable: Throwable) {
                val elapsedMs = Clock.System.now().toEpochMilliseconds() - startedAtMs
                HavnLogger.error("SellerReportsAI", "[$requestId] http exception elapsed=${elapsedMs}ms", throwable)
                if (throwable.isNetworkTimeout()) {
                    throw IllegalStateException("Seller report AI is taking longer than expected. Please try again in a moment.", throwable)
                }
                throw throwable
            }
            val response = httpResponse.body<String>()
            val elapsedMs = Clock.System.now().toEpochMilliseconds() - startedAtMs
            HavnLogger.info(
                "SellerReportsAI",
                "[$requestId] http response status=${httpResponse.status.value} elapsed=${elapsedMs}ms responseChars=${response.length}"
            )
            if (!httpResponse.status.isSuccess()) {
                val error = runCatching {
                    json.decodeFromString(SellerReportAiErrorResponse.serializer(), response).error
                }.getOrNull()
                HavnLogger.warning("SellerReportsAI", "[$requestId] non-success status=${httpResponse.status.value} error=${error ?: response.take(180)}")
                error(error ?: "Unable to generate seller report AI insights right now.")
            }
            val payload = json.decodeFromString<JsonObject>(response)
            HavnLogger.info("SellerReportsAI", "[$requestId] parsed keys=${payload.keys.joinToString(",")}")
            if (!payload.hasSellerReportInsightKeys()) {
                HavnLogger.warning(
                    "SellerReportsAI",
                    "[$requestId] malformed payload missing insight keys keys=${payload.keys.joinToString(",")}"
                )
                error("AI returned report data instead of insights. Please try again in a moment.")
            }
            json.encodeToString(JsonObject.serializer(), payload)
        }.onFailure {
            HavnLogger.error("SellerReportsAI", "Seller report AI insights generation failed", it)
        }
    }
}

private fun sellerReportAiHttpClient(): HttpClient = HttpClient {
    install(HttpTimeout) {
        connectTimeoutMillis = 30_000
        socketTimeoutMillis = 120_000
        requestTimeoutMillis = 120_000
    }
}

private fun sellerReportAiRequestId(): String =
    Clock.System.now().toEpochMilliseconds().toString()

private fun Throwable.isNetworkTimeout(): Boolean {
    val className = this::class.simpleName.orEmpty()
    val timeoutHere = className.contains("Timeout", ignoreCase = true) ||
        message.orEmpty().contains("timeout", ignoreCase = true)
    return timeoutHere || cause?.isNetworkTimeout() == true
}

private val sellerReportInsightKeys = setOf(
    "executive_summary",
    "buyer_sentiment_summary",
    "momentum_analysis",
    "property_strengths",
    "buyer_concerns",
    "agent_activity_summary",
    "recommendations",
    "next_steps",
    "seller_confidence_score"
)

private fun JsonObject.hasSellerReportInsightKeys(): Boolean =
    keys.any { it in sellerReportInsightKeys }

@Serializable
private data class SellerReportAiRequest(
    val language: String,
    val property: SellerReportAiPropertyPayload,
    @SerialName("report_period") val reportPeriod: SellerReportAiReportPeriodPayload,
    @SerialName("open_houses") val openHouses: List<SellerReportAiOpenHousePayload>,
    val totals: SellerReportAiTotalsPayload,
    @SerialName("agent_notes") val agentNotes: List<String>,
    @SerialName("output_required") val outputRequired: JsonElement
)

@Serializable
private data class SellerReportAiPropertyPayload(
    val title: String,
    val address: String,
    val city: String,
    val price: String,
    val status: String,
    @SerialName("days_on_market") val daysOnMarket: Int
)

@Serializable
private data class SellerReportAiReportPeriodPayload(
    val start: String,
    val end: String
)

@Serializable
private data class SellerReportAiOpenHousePayload(
    val date: String,
    val visitors: Int,
    @SerialName("hot_leads") val hotLeads: Int,
    @SerialName("private_tours") val privateTours: Int,
    @SerialName("followups_sent") val followUpsSent: Int
)

@Serializable
private data class SellerReportAiTotalsPayload(
    val visitors: Int,
    @SerialName("hot_leads") val hotLeads: Int,
    @SerialName("warm_leads") val warmLeads: Int,
    @SerialName("cold_leads") val coldLeads: Int,
    @SerialName("private_tours") val privateTours: Int,
    @SerialName("followups_sent") val followUpsSent: Int,
    @SerialName("property_inquiries") val propertyInquiries: Int,
    @SerialName("lead_sources") val leadSources: Map<String, Int>
)

@Serializable
private data class SellerReportAiErrorResponse(val error: String)

private fun SellerReportAiInput.toRequest(): SellerReportAiRequest = SellerReportAiRequest(
    language = language,
    property = SellerReportAiPropertyPayload(
        title = propertyTitle,
        address = propertyAddress,
        city = propertyCity,
        price = propertyPrice,
        status = propertyStatus,
        daysOnMarket = daysOnMarket
    ),
    reportPeriod = SellerReportAiReportPeriodPayload(
        start = reportPeriodStart,
        end = reportPeriodEnd
    ),
    openHouses = openHouses.map { it.toPayload() },
    totals = totals.toPayload(),
    agentNotes = agentNotes,
    outputRequired = requiredOutputJson()
)

private fun SellerReportAiOpenHouse.toPayload(): SellerReportAiOpenHousePayload = SellerReportAiOpenHousePayload(
    date = date,
    visitors = visitors,
    hotLeads = hotLeads,
    privateTours = privateTours,
    followUpsSent = followUpsSent
)

private fun SellerReportAiTotals.toPayload(): SellerReportAiTotalsPayload = SellerReportAiTotalsPayload(
    visitors = visitors,
    hotLeads = hotLeads,
    warmLeads = warmLeads,
    coldLeads = coldLeads,
    privateTours = privateTours,
    followUpsSent = followUpsSent,
    propertyInquiries = propertyInquiries,
    leadSources = leadSources
)

private fun requiredOutputJson(): JsonObject = buildJsonObject {
    listOf(
        "executive_summary",
        "buyer_sentiment_summary",
        "momentum_analysis",
        "property_strengths",
        "buyer_concerns",
        "agent_activity_summary",
        "recommendations",
        "next_steps",
        "seller_confidence_score"
    ).forEach { key -> put(key, JsonPrimitive(true)) }
}
